import { cn } from '@/lib/utils'

interface LoaderProps {
  variant?: 'spin' | 'wave' | 'bars'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
}

export function Loader({ variant = 'bars', size = 'md', className }: LoaderProps) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16',
  }

  const renderLoader = () => {
    switch (variant) {
      case 'spin':
        return (
          <div
            className={cn(
              'animate-spin rounded-full border-2 border-primary border-t-transparent',
              sizeClasses[size],
              className
            )}
          />
        )

      case 'wave':
        return (
          <div className='flex space-x-1'>
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className={cn(
                  'animate-pulse bg-primary rounded-full',
                  size === 'sm' ? 'h-2 w-2' : size === 'lg' ? 'h-3 w-3' : size === 'xl' ? 'h-4 w-4' : 'h-2.5 w-2.5',
                  className
                )}
                style={{
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: '1s',
                }}
              />
            ))}
          </div>
        )

      case 'bars':
        return (
          <div className='flex space-x-1'>
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className={cn(
                  'bg-primary rounded-sm',
                  size === 'sm' ? 'h-3 w-1' : size === 'lg' ? 'h-6 w-1.5' : size === 'xl' ? 'h-8 w-2' : 'h-4 w-1',
                  className
                )}
                style={{
                  animation: `loader-bars 1.2s ease-in-out infinite`,
                  animationDelay: `${i * 0.1}s`,
                }}
              />
            ))}
          </div>
        )

      default:
        return <div className={cn('animate-pulse rounded-full bg-primary', sizeClasses[size], className)} />
    }
  }

  return renderLoader()
}

// Custom keyframes for bars animation
const barsKeyframes = `
@keyframes loader-bars {
  0%, 40%, 100% {
    transform: scaleY(0.4);
  }
  20% {
    transform: scaleY(1);
  }
}
`

// Add the keyframes to the document head
if (typeof document !== 'undefined') {
  const style = document.createElement('style')
  style.textContent = barsKeyframes
  if (!document.head.querySelector('style[data-loader-keyframes]')) {
    style.setAttribute('data-loader-keyframes', 'true')
    document.head.appendChild(style)
  }
}

export const PageLoader = () => {
  return (
    <div className='flex min-h-screen items-center justify-center bg-background' data-testid='page-loader'>
      <div className='text-center'>
        <Loader size='lg' />
      </div>
    </div>
  )
}
